import { useState, type CSSProperties } from 'react';

interface ScoreScaleProps {
  value: number | null;
  onChange: (value: number) => void;
  color: 'primary' | 'accent';
  lowLabel?: string;
  highLabel?: string;
}

const SCALE_POINTS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// 점수 해석 (0~10점 기준)
const SCORE_HINTS = [
  { min: 0, max: 2, label: '전혀 아니다' },
  { min: 3, max: 4, label: '거의 아니다' },
  { min: 5, max: 6, label: '보통이다' },
  { min: 7, max: 8, label: '그렇다' },
  { min: 9, max: 10, label: '매우 그렇다' },
];

export default function ScoreScale({
  value,
  onChange,
  color,
  lowLabel = '전혀 아니다',
  highLabel = '매우 그렇다',
}: ScoreScaleProps) {
  // 드래그 중에는 미리보기 값만 보여주고, 손을 뗐을 때 확정해
  // 다음 문항으로 넘어가는 걸 방지한다.
  const [draft, setDraft] = useState<number | null>(null);

  const hasValue = value !== null || draft !== null;
  const current = draft ?? value ?? 0;
  const pct = (current / 10) * 100;

  const activeColorFull =
    color === 'primary' ? 'oklch(var(--primary-500))' : 'oklch(var(--accent-500))';
  const activeText = color === 'primary' ? 'text-primary-700' : 'text-accent-700';
  const activeBg = color === 'primary' ? 'bg-primary-500' : 'bg-accent-500';

  const hint = SCORE_HINTS.find((h) => current >= h.min && current <= h.max);

  const sliderStyle = {
    background: `linear-gradient(to right, ${activeColorFull} 0%, ${activeColorFull} ${pct}%, oklch(var(--background-200)) ${pct}%, oklch(var(--background-200)) 100%)`,
    '--score-color': activeColorFull,
  } as CSSProperties;

  // 드래그 완료 시 확정
  const commit = () => {
    if (draft !== null) {
      onChange(draft);
      setDraft(null);
    }
  };

  return (
    <div className="select-none">
      {/* 현재 점수 표시 */}
      <div className="flex items-end justify-center mb-1">
        <span
          className={`font-heading text-5xl md:text-6xl font-bold leading-none transition-colors ${
            hasValue ? activeText : 'text-foreground-300'
          }`}
        >
          {hasValue ? current : '—'}
        </span>
        <span className="text-sm text-foreground-400 mb-1 ml-1.5">/ 10점</span>
      </div>

      {/* 점수 해석 안내 */}
      <div className="flex justify-center mb-5 h-5">
        {hasValue && hint && (
          <span className="text-xs font-medium text-foreground-500">{hint.label}</span>
        )}
        {!hasValue && (
          <span className="text-xs text-foreground-400">0점부터 10점 사이에서 선택해주세요</span>
        )}
      </div>

      {/* 슬라이더 */}
      <input
        type="range"
        min={0}
        max={10}
        step={1}
        value={current}
        onChange={(e) => setDraft(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="score-range w-full"
        style={sliderStyle}
        aria-label="0점부터 10점까지 점수 선택"
      />

      {/* 숫자 눈금 */}
      <div className="flex justify-between mt-2.5 px-0.5">
        {SCALE_POINTS.map((point) => (
          <button
            key={point}
            type="button"
            onClick={() => {
              setDraft(null);
              onChange(point);
            }}
            aria-label={`${point}점 선택`}
            className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              hasValue && current === point
                ? `${activeBg} text-white scale-110`
                : 'text-foreground-400 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            {point}
          </button>
        ))}
      </div>

      {/* 양 끝 라벨 */}
      <div className="flex justify-between mt-2 text-xs text-foreground-400">
        <span>0 · {lowLabel}</span>
        <span>{highLabel} · 10</span>
      </div>
    </div>
  );
}